import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

const TOP_N_OPTIONS = [5, 10, 15, 20];

interface EtfHoldingItem {
    shares: number;
    weight: number;
    asset_name?: string;
    etf_symbol: string;
    updated_at?: string;
    asset_symbol?: string;
    market_value?: number;
}

interface StockEtfDistributionData {
    symbol: string;
    top_n: number;
    top_etfs: EtfHoldingItem[];
    bottom_etfs: EtfHoldingItem[];
    total_shares: number;
    total_market_value: number;
    total_etfs_with_stock: number;
}

// Stock Distribution Across ETF’s
// TraderverseStockDistributionAcrossETF
export class TraderverseStockDistributionAcrossETF {
    /** Min vertical space per ETF row so y-axis tickers do not collide. */
    private static readonly ROW_HEIGHT = 24;
    private static readonly BAR_HEIGHT = 12;
    private static readonly GRID_CHROME = 78;
    private static readonly MIN_CHART_HEIGHT = 220;

    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: StockEtfDistributionData | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
    private chartInstances: any[] = [];
    private resizeRedrawTimer: number | null = null;
    private filters = {
        topN: 10
    };

    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        this.filters.topN = Number((config as any).topN ?? (this.config as any).topN ?? this.filters.topN);
        (this.config as any).topN = this.filters.topN;

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            if (this.resizeRedrawTimer != null) {
                clearTimeout(this.resizeRedrawTimer);
            }
            this.resizeRedrawTimer = window.setTimeout(() => {
                this.resizeRedrawTimer = null;
                this.updateDisplay();
            }, 80);
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
            headerPinned: true,
            symbol: 'NVDA',
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            topN: 10
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private renderTopNOptions(): string {
        const options = TOP_N_OPTIONS.includes(this.filters.topN)
            ? TOP_N_OPTIONS
            : [...TOP_N_OPTIONS, this.filters.topN].sort((a, b) => a - b);
        return options
            .map(n => `<option value="${n}"${n === this.filters.topN ? ' selected' : ''}>${n}</option>`)
            .join('');
    }

    private filtersIconSvg(): string {
        return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
    }

    private async init(): Promise<void> {
        this.attachStyles();
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Stock Distribution Across ETF’s</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                  ${this.config.symbol
                    ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                    : '<span>Select Symbol</span>'}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`}
              <span></span>
              <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()} Filters</button>
              <div class="filtersBox sideBar hidden">
                <div class="item">
                  <label>Top N</label>
                  <select class="topn-select" aria-label="Top N" data-testing-id="topn-select">${this.renderTopNOptions()}</select>
                </div>
              </div>
            </div>
            <div class="chartDv" id="stockEtfDistribution" data-testing-id="stock-etf-distribution" style="display:flex; gap:16px; min-height:0;">
              <div id="highestChartContainer" class="chart" style="flex:1; min-width:0; height:100%; position:relative;" data-testing-id="highest-etf-chart"></div>
              <div id="lowestChartContainer" class="chart" style="flex:1; min-width:0; height:100%; position:relative;" data-testing-id="lowest-etf-chart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);
        this.setupEventListeners();
        this.wireFilterControls();
    }

    private setupEventListeners(): void {
        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }
    }

    private onSelectChange(stock: any): void {
        this.changeSymbol(stock.symbol, stock);
    }

    private wireFilterControls(): void {
        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const topNSelect = this.shadowRoot.querySelector('.topn-select') as HTMLSelectElement | null;

        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        topNSelect?.addEventListener('change', () => {
            this.filters.topN = Number(topNSelect.value || this.filters.topN);
            (this.config as any).topN = this.filters.topN;
            this.setupChart(this.config.symbol!);
        });
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private normalizeHoldings(list: any[]): EtfHoldingItem[] {
        if (!Array.isArray(list)) return [];
        return list
            .map((item) => ({
                shares: Number(item?.shares ?? 0),
                weight: Number(item?.weight ?? 0),
                asset_name: item?.asset_name,
                etf_symbol: String(item?.etf_symbol ?? ''),
                updated_at: item?.updated_at,
                asset_symbol: item?.asset_symbol,
                market_value: item?.market_value != null ? Number(item.market_value) : undefined
            }))
            .filter((item) => item.etf_symbol);
    }

    private async setupChart(symbol: string): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        try {
            if (widgetBox) widgetBox.classList.add('loading');

            const topN = this.filters.topN ?? 10;
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/etf/stock-distribution`,
                query: {
                    symbol,
                    topN
                },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            if (json && typeof json.isSuccess === 'boolean' && !json.isSuccess) {
                throw new Error(json.message || 'API returned isSuccess: false');
            }

            const dataBlock = json?.data ?? {};
            this.currentData = {
                symbol: dataBlock.symbol ?? symbol,
                top_n: Number(dataBlock.top_n ?? topN),
                top_etfs: this.normalizeHoldings(dataBlock.top_etfs),
                bottom_etfs: this.normalizeHoldings(dataBlock.bottom_etfs),
                total_shares: Number(dataBlock.total_shares ?? 0),
                total_market_value: Number(dataBlock.total_market_value ?? 0),
                total_etfs_with_stock: Number(dataBlock.total_etfs_with_stock ?? 0)
            };

            if (!this.currentData.top_etfs.length && !this.currentData.bottom_etfs.length) {
                widgetBox?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error('Error fetching chart data:', err);
            widgetBox?.removeAttribute('data-testing-id');
            this.currentData = null;
            this.updateDisplay();
        } finally {
            widgetBox?.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawCharts();
    }

    private disposeCharts(): void {
        const echarts = (window as any).echarts;
        this.chartInstances.forEach((chart) => {
            try { chart?.dispose?.(); } catch { /* ignore */ }
        });
        this.chartInstances = [];

        ['#highestChartContainer', '#lowestChartContainer'].forEach((selector) => {
            const el = this.shadowRoot.querySelector(selector) as HTMLElement | null;
            if (!el || !echarts) return;
            const existing = echarts.getInstanceByDom(el);
            if (existing) echarts.dispose(existing);
        });
    }

    private sortForChart(items: EtfHoldingItem[]): EtfHoldingItem[] {
        // ECharts category axis: first item renders at the bottom — keep largest at bottom.
        return [...items].sort((a, b) => b.shares - a.shares);
    }

    private formatSharesMillions(value: number): string {
        const millions = value / 1_000_000;
        if (millions >= 100) return millions.toFixed(0);
        if (millions >= 1) return millions.toFixed(2);
        if (millions >= 0.01) return millions.toFixed(2);
        return millions.toFixed(4);
    }

    private getChartHeight(barCount: number): number {
        return Math.max(
            TraderverseStockDistributionAcrossETF.MIN_CHART_HEIGHT,
            barCount * TraderverseStockDistributionAcrossETF.ROW_HEIGHT
                + TraderverseStockDistributionAcrossETF.GRID_CHROME
        );
    }

    private isFillParentHeight(): boolean {
        const height = this.config.height;
        if (typeof height === 'number') return false;
        return String(height ?? '').trim().endsWith('%');
    }

    private updateChartLayout(barCount: number): void {
        const chartHeight = this.getChartHeight(Math.max(barCount, 1));
        const chartDv = this.shadowRoot.querySelector('#stockEtfDistribution') as HTMLElement | null;
        const highestEl = this.shadowRoot.querySelector('#highestChartContainer') as HTMLElement | null;
        const lowestEl = this.shadowRoot.querySelector('#lowestChartContainer') as HTMLElement | null;
        const inBody = this.shadowRoot.querySelector('.inBody') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
        const headTop = this.shadowRoot.querySelector('.headTop') as HTMLElement | null;
        const optionsRow = this.shadowRoot.querySelector('.optionsRow') as HTMLElement | null;

        if (chartDv) {
            chartDv.style.height = `${chartHeight}px`;
            chartDv.style.minHeight = `${chartHeight}px`;
            chartDv.style.flex = '0 0 auto';
        }

        [highestEl, lowestEl].forEach((el) => {
            if (!el) return;
            el.style.height = `${chartHeight}px`;
            el.style.minHeight = `${chartHeight}px`;
        });

        // Percent-height embeds (feed grid): fill the cell and scroll if rows need more room.
        if (this.isFillParentHeight()) {
            const headH = headTop?.offsetHeight ?? 56;
            const boxH = widgetBox?.clientHeight ?? 0;
            if (inBody && boxH > 0) {
                inBody.style.height = `${Math.max(120, boxH - headH)}px`;
                inBody.style.minHeight = '0';
                inBody.style.overflow = 'auto';
            }
            return;
        }

        if (this.config.autosize === false) return;

        const optionsRowHeight = optionsRow?.offsetHeight ?? 50;
        const headTopHeight = headTop?.offsetHeight ?? 56;
        const inBodyPadding = 32;
        const inBodyHeight = optionsRowHeight + chartHeight + inBodyPadding;

        if (inBody) {
            inBody.style.height = `${inBodyHeight}px`;
            inBody.style.minHeight = `${inBodyHeight}px`;
        }
        if (widgetBox) {
            widgetBox.style.height = `${headTopHeight + inBodyHeight}px`;
        }
    }

    private buildChartOption(
        title: string,
        items: EtfHoldingItem[],
        barColor: string,
        widgetBox: HTMLElement
    ): any {
        const chartData = this.sortForChart(items);
        const yAxisData = chartData.map((item) => item.etf_symbol);
        const values = chartData.map((item) => +(item.shares / 1_000_000).toFixed(6));
        const maxValue = Math.max(...values, 0);
        const rowHeight = TraderverseStockDistributionAcrossETF.ROW_HEIGHT;
        const barHeight = TraderverseStockDistributionAcrossETF.BAR_HEIGHT;

        const widgetStyles = getComputedStyle(widgetBox);
        const textColor = widgetStyles.getPropertyValue('--charts-text-color');
        const axisLineColor = widgetStyles.getPropertyValue('--charts-axis-color');
        const splitLineColor = widgetStyles.getPropertyValue('--charts-split-line-color');
        const fontSizeToken = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const labelFontSize = Math.min(parseInt(fontSizeToken, 10) || 12, rowHeight - 8);

        return {
            backgroundColor: 'transparent',
            title: {
                text: title,
                left: 'center',
                top: 0,
                textStyle: {
                    color: textColor,
                    fontSize: labelFontSize,
                    fontWeight: 500
                }
            },
            tooltip: {
                trigger: 'item',
                confine: true,
                appendToBody: false,
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const index = params.dataIndex;
                    const item = chartData[index];
                    if (!item) return '';
                    return this.formatSharesMillions(item.shares);
                }
            },
            grid: { left: 4, right: 20, top: 32, bottom: 40, containLabel: true },
            xAxis: {
                type: 'value',
                name: 'Shares (Millions)',
                nameLocation: 'middle',
                nameGap: 26,
                min: 0,
                max: maxValue > 0 ? undefined : 1,
                nameTextStyle: {
                    color: textColor,
                    fontSize: labelFontSize
                },
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    margin: 10,
                    hideOverlap: true,
                    formatter: (value: number) => {
                        if (Math.abs(value) >= 100) return String(Math.round(value));
                        if (Math.abs(value) >= 1) return value.toFixed(0);
                        if (Math.abs(value) >= 0.01) return value.toFixed(2);
                        if (value === 0) return '0';
                        return value.toFixed(2);
                    }
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dashed'
                    }
                },
                axisTick: { show: false },
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisLineColor
                    }
                }
            },
            yAxis: {
                type: 'category',
                data: yAxisData,
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    interval: 0,
                    margin: 10,
                    lineHeight: rowHeight,
                    height: rowHeight,
                    overflow: 'truncate',
                    width: 64,
                    align: 'right',
                    verticalAlign: 'middle'
                },
                axisTick: {
                    show: false
                },
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisLineColor
                    }
                },
                splitLine: {
                    show: false
                }
            },
            series: [
                {
                    name: 'Shares (Millions)',
                    type: 'bar',
                    data: values,
                    barWidth: barHeight,
                    barMaxWidth: barHeight,
                    barCategoryGap: Math.max(4, rowHeight - barHeight),
                    itemStyle: {
                        borderRadius: [0, 100, 100, 0],
                        color: barColor
                    },
                    label: {
                        show: false
                    }
                }
            ],
            animation: true,
            animationDuration: 800,
            animationEasing: 'cubicOut'
        };
    }

    private drawCharts(): void {
        const echarts = (window as any).echarts;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#stockEtfDistribution') as HTMLElement;
        const highestEl = this.shadowRoot.querySelector('#highestChartContainer') as HTMLDivElement;
        const lowestEl = this.shadowRoot.querySelector('#lowestChartContainer') as HTMLDivElement;
        if (!highestEl || !lowestEl || !widgetBox) return;

        this.disposeCharts();

        const topEtfs = this.currentData?.top_etfs ?? [];
        const bottomEtfs = this.currentData?.bottom_etfs ?? [];
        const hasData = topEtfs.length > 0 || bottomEtfs.length > 0;

        if (hasData) {
            containerWrapper?.setAttribute('data-testing-id', 'stock-etf-distribution');
        } else {
            containerWrapper?.removeAttribute('data-testing-id');
            highestEl.innerHTML = getNoDataHTML();
            lowestEl.innerHTML = '';
            return;
        }

        this.updateChartLayout(Math.max(topEtfs.length, bottomEtfs.length));

        const upColor = getComputedStyle(widgetBox).getPropertyValue('--up').trim() || '#34e36f';
        const downColor = getComputedStyle(widgetBox).getPropertyValue('--down').trim() || '#ff3b3b';

        if (topEtfs.length) {
            const highestChart = echarts.init(highestEl);
            highestChart.setOption(this.buildChartOption('Highest', topEtfs, upColor, widgetBox));
            this.chartInstances.push(highestChart);
        } else {
            highestEl.innerHTML = getNoDataHTML();
        }

        if (bottomEtfs.length) {
            const lowestChart = echarts.init(lowestEl);
            lowestChart.setOption(this.buildChartOption('Lowest', bottomEtfs, downColor, widgetBox));
            this.chartInstances.push(lowestChart);
        } else {
            lowestEl.innerHTML = getNoDataHTML();
        }
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (id: any) => this.setupChart(id),
            (type: any, data: any) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.resizeRedrawTimer != null) {
            clearTimeout(this.resizeRedrawTimer);
            this.resizeRedrawTimer = null;
        }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        this.disposeCharts();
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseStockDistributionAcrossETF: typeof TraderverseStockDistributionAcrossETF; } }
if (typeof window !== 'undefined') { window.TraderverseStockDistributionAcrossETF = TraderverseStockDistributionAcrossETF; }
